import { JobTemplateFieldsFragment } from '~/gql/graphql';

/**
 * System-generated template naming convention suffix
 */
const SYSTEM_SUFFIX = ' [System]';

/**
 * Checks if a template name indicates it's system-generated
 * System-generated templates end with " [System]" or " [System] (number)"
 * Examples:
 * - "Job Name [System]" ✓
 * - "Job Name [System] (2)" ✓
 * - "Job Name [System] (100)" ✓
 */
export function isSystemGeneratedTemplate(
  template: { name: string } | string
): boolean {
  const name = typeof template === 'string' ? template : template.name;
  const trimmedName = name.trim();

  // Match base pattern: " [System]"
  if (trimmedName.endsWith(SYSTEM_SUFFIX)) {
    return true;
  }

  // Match numbered variants: " [System] (number)"
  // Pattern matches: [System] followed by optional space and (number)
  const numberedSystemPattern = /\s+\[System\]\s+\(\d+\)$/;
  return numberedSystemPattern.test(trimmedName);
}

/**
 * Normalizes a template name by removing the system suffix and template suffix
 * This matches the logic used in useTemplateManager for comparison
 * Also handles backend-generated template names like "Template for \"{name}\" (timestamp)"
 * Also handles numbered system templates like "Name [System] (2)"
 */
export function normalizeTemplateName(name: string): string {
  return name
    .replace(/^Template\s+for\s+"([^"]+)"\s*\([^)]+\)$/i, '$1') // Remove "Template for \"{name}\" (timestamp)" pattern
    .replace(/\s*template[-_\s]*\S*/gi, '') // Remove "template" suffix
    .replace(/\s+\[System\](\s+\(\d+\))?$/, '') // Remove " [System]" or " [System] (number)" suffix
    .trim();
}

/**
 * Compares two arrays for equality (order-sensitive)
 */
function arraysEqual(
  arr1: readonly string[] = [],
  arr2: readonly string[] = []
): boolean {
  if (arr1.length !== arr2.length) return false;
  for (let i = 0; i < arr1.length; i++) {
    if (arr1[i] !== arr2[i]) return false;
  }
  return true;
}

/**
 * Job configuration extracted from a job form
 */
export type JobConfig = {
  name: string;
  notes?: string | null;
  tags?: string[] | null;
  includeAttributes?: string[] | null;
  taskCount: number; // Count of active tasks (excluding _destroy: true)
};

/**
 * Finds a matching template from a list of templates based on job configuration
 * Uses the same comparison logic as useTemplateManager.isTemplateModified (create mode)
 *
 * @param jobConfig - The job configuration to match against
 * @param templates - List of templates to search (can be readonly)
 * @returns The matching template or null if no match found
 */
export function findMatchingTemplate(
  jobConfig: JobConfig,
  templates: readonly JobTemplateFieldsFragment[]
): JobTemplateFieldsFragment | null {
  const normalizedJobName = normalizeTemplateName(jobConfig.name);
  const jobNotes = jobConfig.notes || '';
  const jobTags = (jobConfig.tags || []) as string[];
  const jobAttributes = (jobConfig.includeAttributes || []) as string[];

  for (const template of templates) {
    // Normalize template name (remove "template" suffix and " [System]" suffix)
    const templateName = normalizeTemplateName(template.name);
    const templateNotes = template.notes || '';
    const templateTags = (template.tags?.map(({ id }) => id) || []) as string[];
    const templateAttributes = (template.includeAttributes || []) as string[];
    const templateTaskCount = template.tasks?.length ?? 0;

    // Compare all fields using create mode logic (exact match required)
    const nameMatches = normalizedJobName === templateName;
    const notesMatch = jobNotes === templateNotes;
    const tagsMatch = arraysEqual(jobTags, templateTags);
    const attributesMatch = arraysEqual(jobAttributes, templateAttributes);
    const taskCountMatches = jobConfig.taskCount === templateTaskCount;

    if (
      nameMatches &&
      notesMatch &&
      tagsMatch &&
      attributesMatch &&
      taskCountMatches
    ) {
      return template;
    }
  }

  return null;
}
